import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Card } from "@/components/ui/card";
import { Loader2, Flame, TrendingUp, Award, Lock, Unlock, Receipt, Sparkles } from "lucide-react";
import { getMyScore, listMyEvents, type DisciplineEvent, type DisciplineScore } from "@/domains/discipline";
import { toast } from "sonner";

const KIND_META: Record<string, { label: string; icon: typeof Lock; tone: string }> = {
  vault_lock: { label: "Locked into vault", icon: Lock, tone: "text-primary" },
  vault_auto_save: { label: "Auto-saved", icon: Sparkles, tone: "text-accent" },
  vault_unlock: { label: "Unlocked from vault", icon: Unlock, tone: "text-muted-foreground" },
  bill_paid: { label: "Paid bill split", icon: Receipt, tone: "text-primary" },
};

const Discipline = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState<DisciplineScore | null>(null);
  const [events, setEvents] = useState<DisciplineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, e] = await Promise.all([getMyScore(), listMyEvents(30)]);
        setScore(s);
        setEvents(e);
      } catch (err) {
        toast.error((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tier =
    !score ? "—" :
    score.total_points >= 200 ? "Disciplined" :
    score.total_points >= 75 ? "Steady" :
    score.total_points >= 20 ? "Building" : "Starter";

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)} curve>
        <h1 className="text-primary-foreground text-2xl font-display font-bold">Discipline</h1>
      </BrandHeader>

      <div className="-mt-6 mx-4 bg-background rounded-2xl shadow-card p-5 space-y-4">
        {loading ? (
          <div className="grid place-items-center py-10 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Card className="p-3 text-center">
                <TrendingUp className="h-4 w-4 mx-auto text-primary mb-1" />
                <div className="text-xl font-bold">{score?.total_points ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Points</div>
              </Card>
              <Card className="p-3 text-center">
                <Flame className="h-4 w-4 mx-auto text-accent mb-1" />
                <div className="text-xl font-bold">{score?.current_streak_days ?? 0}d</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Streak</div>
              </Card>
              <Card className="p-3 text-center">
                <Award className="h-4 w-4 mx-auto text-primary mb-1" />
                <div className="text-sm font-bold leading-tight pt-0.5">{tier}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tier</div>
              </Card>
            </div>

            <Card className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
              <div className="text-sm font-semibold mb-1">How to earn</div>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Lock money into a vault — <span className="text-primary font-semibold">+5 pts</span></li>
                <li>• Auto-save from incoming — <span className="text-primary font-semibold">+3 pts</span></li>
                <li>• Pay your bill split share — <span className="text-primary font-semibold">+4 pts</span></li>
                <li>• Unlock from vault — <span className="text-destructive font-semibold">-2 pts</span></li>
              </ul>
            </Card>

            <div>
              <div className="text-sm font-semibold mb-2">Recent activity</div>
              {events.length === 0 ? (
                <Card className="p-6 text-center text-sm text-muted-foreground">
                  No activity yet. Start a vault to build discipline.
                </Card>
              ) : (
                <div className="space-y-2">
                  {events.map((e) => {
                    const meta = KIND_META[e.kind] ?? { label: e.kind, icon: Sparkles, tone: "text-muted-foreground" };
                    const Icon = meta.icon;
                    const cents = Number((e.meta as { cents?: number })?.cents ?? 0);
                    return (
                      <Card key={e.id} className="p-3 flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-full bg-muted grid place-items-center ${meta.tone}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{meta.label}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(e.created_at).toLocaleString()}
                            {cents > 0 && ` · $${(cents / 100).toFixed(2)}`}
                          </div>
                        </div>
                        <div className={`text-sm font-bold ${e.points >= 0 ? "text-primary" : "text-destructive"}`}>
                          {e.points >= 0 ? "+" : ""}{e.points}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </MobileShell>
  );
};

export default Discipline;
